/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { companyEnvironments, companyThreads, threads, workflowRunListPaging, workflows, workflowsLoadedForActiveCompany } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import getThreadId from '@/lib/threads/getThreadId.js';
import matchesThreadListScope from '@/lib/threads/matchesThreadListScope.js';
import { param as selectedParam, query as selectedQuery } from '@/redux/selectors/uiSelectors.js';

/* A workflow answers to two ids. `id` belongs to one version of it, so it changes every time the
   workflow is published, while `workflowId` stays with the workflow for its whole life. Links the
   app builds carry the version id, and links we mail carry the durable one — a notification sitting
   in someone's inbox has to still open the workflow months and several versions later. Both resolve
   here so neither kind of link goes to an empty page. */
export const currentWorkflow = createSelector([workflows, (state) => selectedParam(state, 'automationId')], (workflowList, id) => {
  if (!id) {
    return null;
  }

  return workflowList.find((w) => w?.id === id || w?.workflowId === id) || null;
});

/* A workflow only gets a `workflow_snapshot` row once it clears the requirements gate and
   `workflow_create` runs, so the snapshot-backed catalog cannot list one that is still being
   planned — 97 live builds were invisible that way on 2026-08-31, which is what made "New workflow,
   navigate away, gone" read as data loss. Until the snapshot exists the authoring thread IS the
   record, so a planning build is listed from its thread: it has no `workflowId`, nothing to
   activate, and opening it goes to the thread rather than an automation detail page that has no
   workflow to render. The viewer's own threads list only threads they own, so a build shared with
   the organization while still planning reaches everyone else through the company thread list;
   the owner has it in both, hence one row per thread id. That list is stored as the API returns it,
   keyed `threadId`, while the viewer's own threads are normalized to `id` — getThreadId reads both. */
export const automationWorkflows = createSelector([workflows, threads, companyThreads, workflowsLoadedForActiveCompany], (workflowList, threadList, companyThreadList, hasLoadedWorkflows) => {
  const draftWorkflows = workflowList.filter((workflow) => workflow?.version === 'draft');

  if (!hasLoadedWorkflows) {
    return draftWorkflows;
  }

  const listedThreadIds = new Set(draftWorkflows.map((workflow) => workflow?.threadId).filter(Boolean));

  const planningWorkflows = [...threadList, ...companyThreadList]
    .filter((thread) => {
      const threadId = getThreadId(thread);

      if (!threadId || !matchesThreadListScope({ thread, threadListScope: 'workflows' }) || listedThreadIds.has(threadId)) {
        return false;
      }

      listedThreadIds.add(threadId);
      return true;
    })
    .map((thread) => ({
      id: getThreadId(thread),
      threadId: getThreadId(thread),
      workflowId: null,
      title: thread.title,
      version: 'draft',
      active: false,
      isCompany: thread.isCompany === true,
      isPlanning: true,
      updatedOn: thread.updatedAt
    }));

  return [...draftWorkflows, ...planningWorkflows];
});

export const currentAutomationWorkflowRunId = (state) => String(selectedQuery(state, 'run') || '').trim() || null;

// Saved output for one step, from `GET /workflows/:id/steps/:stepId/output`. Written by single-step test
// runs, so a step that has only ever run as part of a full workflow has none; the step's own
// `meta.output` says when what is stored here was produced.
export const workflowStepOutput = (state, stepId) => {
  return state.pieve.workflowStepOutputs.find((row) => row.stepId === stepId)?.data?.output ?? null;
};

// How the step's saved output was produced (its provenance stamp) and whether the step's settings changed since.
export const workflowStepOutputProvenance = createSelector([(state, stepId) => state.pieve.workflowStepOutputs.find((row) => row.stepId === stepId)?.data], (data) => {
  return data ? { provenance: data.provenance ?? null, configDiffers: data.configDiffers === true } : null;
});

/* The run history's environment choices: every environment the window's runs used, which the server reports whatever
   environment is picked, so the choices stay while one narrows the list, plus the picked one even when the window has
   no run there. An id with no environment row (deleted) is shown by its id. */
export const workflowRunEnvironmentFilterOptions = createSelector([(state, workflowId) => workflowRunListPaging(state, workflowId)?.environmentIds, (state) => selectedQuery(state, 'environment'), companyEnvironments], (environmentIds, pickedEnvironmentId, environments) => {
  if (!Array.isArray(environmentIds)) {
    return EMPTY_ARRAY;
  }

  const ids = pickedEnvironmentId && !environmentIds.includes(pickedEnvironmentId) ? [...environmentIds, pickedEnvironmentId] : environmentIds;

  return [{ value: '', label: 'All environments' }, ...ids.map((id) => ({ value: id, label: environments.find((environment) => environment.id === id)?.name || id }))];
});
